import React, { useMemo } from 'react';
import MenuItemModal from './MenuItemModal/MenuItemModal';
import {
  cartLineCanCustomise,
  useCartCustomise,
} from './CartCustomiseController';
import {
  findMenuItemForCartLine,
  menuItemIsCustomisable,
} from '../pages/OurMenu/ourMenuUtils';
import { subscriptionCartLineKey } from '../utils/subscriptionCartItems';
import './AlreadyInCartSheet/AlreadyInCartSheet.css';

/**
 * Opens product details to edit options / add-ons on a meal-plan (Our menu) line.
 * Keeps the line’s current meal slot; saves via MenuPlanAction + replaceSubscriptionCartLine.
 */
export default function PlanCustomiseController({
  planLine,
  open,
  onClose,
  menuItems = [],
  menuStatus = 'idle',
}) {
  const menuItem = useMemo(
    () => (planLine ? findMenuItemForCartLine(menuItems, planLine) : null),
    [menuItems, planLine],
  );

  const editPlanLineKey = planLine ? subscriptionCartLineKey(planLine) : null;

  const initialVariantId = useMemo(() => {
    if (!planLine || !menuItem) return null;
    if (menuItem.kind !== 'variable') return null;
    return Number(planLine.id);
  }, [planLine, menuItem]);

  const initialAddOnIds = useMemo(
    () =>
      (planLine?.addOns || [])
        .map((a) => Number(a.id ?? a.addOnItemID))
        .filter((id) => Number.isFinite(id) && id > 0),
    [planLine],
  );

  const initialAddOnQtys = useMemo(() => {
    /** @type {Record<number, number>} */
    const map = {};
    for (const a of planLine?.addOns || []) {
      const id = Number(a.id ?? a.addOnItemID);
      if (!Number.isFinite(id) || id <= 0) continue;
      map[id] = Math.max(1, Math.round(Number(a.quantity) || 1));
    }
    return map;
  }, [planLine]);

  if (!open || !planLine) return null;

  if (menuStatus === 'loading' || menuStatus === 'idle') {
    return (
      <div className="aics-overlay" onClick={onClose} role="presentation">
        <div className="aics-sheet cart-customise-error" role="status" onClick={(e) => e.stopPropagation()}>
          <p>Loading customisation…</p>
          <button type="button" className="aics-btn aics-btn--ghost" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    );
  }

  if (menuStatus === 'error' || !menuItem) {
    return (
      <div className="aics-overlay" onClick={onClose} role="presentation">
        <div className="aics-sheet cart-customise-error" role="alert" onClick={(e) => e.stopPropagation()}>
          <p>
            {menuStatus === 'error'
              ? 'Could not load menu to edit this item. Try again from Our Menu.'
              : 'This item is no longer on the menu.'}
          </p>
          <button type="button" className="aics-btn aics-btn--outline" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    );
  }

  if (!menuItemIsCustomisable(menuItem)) {
    return (
      <div className="aics-overlay" onClick={onClose} role="presentation">
        <div className="aics-sheet cart-customise-error" role="status" onClick={(e) => e.stopPropagation()}>
          <p>No options or add-ons to change for this item.</p>
          <button type="button" className="aics-btn aics-btn--outline" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <MenuItemModal
      item={menuItem}
      onClose={onClose}
      openIntent="plan"
      editPlanLineKey={editPlanLineKey}
      editSlotId={planLine.slotId || null}
      initialVariantId={initialVariantId}
      initialAddOnIds={initialAddOnIds}
      initialAddOnQtys={initialAddOnQtys}
      initialQuantity={Math.max(1, Number(planLine.quantity) || 1)}
    />
  );
}

export function planLineCanCustomise(line, menuItems, menuStatus) {
  return cartLineCanCustomise(line, menuItems, menuStatus);
}

export function usePlanCustomise(planItems) {
  return useCartCustomise(planItems);
}
